import { Select } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { useAssignableUsers } from '@/shared/hooks/use-users'
import { cn } from '@/shared/lib/cn'

/** Valor de filtro para "proyectos y tareas sin responsable". */
export const UNASSIGNED = 'sin-asignar'

export interface AssigneePickerProps {
  /** `null` = sin responsable (asignacion) o sin filtro (modo filtro). */
  value: string | null
  onChange: (userId: string | null) => void
  /** Etiqueta accesible: en las filas densas no hay label visible. Default: "Responsable". */
  label?: string
  /**
   * En modo filtro el vacio significa "cualquiera" y aparece ademas la opcion
   * explicita "Sin responsable", que es un filtro util por si mismo (uno de los
   * cuatro KPIs del portafolio).
   */
  asFilter?: boolean
  /**
   * Acota las opciones a la empresa de ese proyecto. Es el caso de la ficha de
   * una tarea: ofrecer a alguien que no tiene acceso a esa empresa sería
   * ofrecer una opción que el servidor rechaza.
   */
  projectId?: string | null
  disabled?: boolean
  className?: string
}

/**
 * El selector de responsable.
 *
 * Las opciones salen de `useAssignableUsers`, que pega a `GET /users/assignable`
 * —interno, activo y con acceso a la empresa—, y **no** de `GET /users`, que es
 * de admin y lista a todo el mundo, clientes incluidos. Ver el docstring del
 * hook: cambiarlo de vuelta reintroduce los dos bugs a la vez.
 */
export function AssigneePicker({
  value,
  onChange,
  label,
  asFilter = false,
  projectId,
  disabled,
  className,
}: AssigneePickerProps) {
  const t = useT()
  const { data: users = [], isPending } = useAssignableUsers(projectId)

  return (
    <Select
      aria-label={label ?? t('common.label.owner')}
      disabled={disabled || isPending}
      className={cn('w-auto min-w-[150px]', className)}
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
    >
      <option value="">{asFilter ? t('tasks.assignee.any') : t('common.label.noOwner')}</option>
      {asFilter ? <option value={UNASSIGNED}>{t('common.label.noOwner')}</option> : null}
      {users.map((user) => (
        <option key={user.id} value={user.id}>
          {user.display_name}
        </option>
      ))}
    </Select>
  )
}
